import Decimal from 'decimal.js'
import makerjs from 'makerjs'
import { CutoutGenerator } from './CutoutGenerator'

// Basic MX stabilizer cutout

export class StabilizerMXBasic extends CutoutGenerator {

    generate(key, generatorOptions) {

        let keySize = key.width

        if (!key.skipOrientationFix && key.height > key.width) {
            keySize = key.height
        }

        let stab_spacing_left = null
        let stab_spacing_right = null

        if (keySize.gte(8)) {
            stab_spacing_left = stab_spacing_right = new Decimal("66.675")
        }
        else if (keySize.gte(7)) {
            stab_spacing_left = stab_spacing_right = new Decimal("57.15")
        }
        else if (keySize.gte(6.25)) {
            stab_spacing_left = stab_spacing_right = new Decimal("50")
        }
        else if (keySize.gte(6)) {
            if (key.shift6UStabilizers) {
                stab_spacing_left = new Decimal("57.15")
                stab_spacing_right = new Decimal("38.1")
            } else {
                stab_spacing_left = stab_spacing_right = new Decimal("47.625")
            }
        }
        else if (keySize.gte(4.5)) {
            stab_spacing_left = stab_spacing_right = new Decimal("35")
        }
        else if (keySize.gte(4.25)) {
            stab_spacing_left = stab_spacing_right = new Decimal("30")
        }
        else if (keySize.gte(3)) {
            stab_spacing_left = stab_spacing_right = new Decimal("19.05")
        }
        else if (keySize.gte(2)) {
            stab_spacing_left = stab_spacing_right = new Decimal("11.938")
        }
        else {
            return null
        }

        const width = new Decimal("7")
        const upperBound = new Decimal("6")
        const lowerBound = new Decimal("-9")
        const connectorHeight = new Decimal("4")

        const plusHalfWidth = width.dividedBy(new Decimal("2"))
        const minsHalfWidth = width.dividedBy(new Decimal("-2"))
        const kerf = generatorOptions.kerf

        // Outer / inner X coordinates (with kerf)
        // Left upright
        const leftOuterX  = stab_spacing_left.times(-1).plus(minsHalfWidth).plus(kerf)   // leftmost
        const leftInnerX  = stab_spacing_left.times(-1).plus(plusHalfWidth).minus(kerf)  // right edge of left upright
        // Right upright
        const rightInnerX = stab_spacing_right.plus(minsHalfWidth).plus(kerf)            // left edge of right upright
        const rightOuterX = stab_spacing_right.plus(plusHalfWidth).minus(kerf)           // rightmost

        // Y coordinates (with kerf)
        const topY          = upperBound.minus(kerf)
        const connectorTopY = lowerBound.plus(connectorHeight).minus(kerf)
        const bottomY       = lowerBound.plus(kerf)

        // Points for the single continuous outline (counter-clockwise, starting bottom-left)
        // 1. bottom-left outer
        const p1 = [leftOuterX.toNumber(),  bottomY.toNumber()]
        // 2. top-left outer
        const p2 = [leftOuterX.toNumber(),  topY.toNumber()]
        // 3. top-left inner (right side of left upright top)
        const p3 = [leftInnerX.toNumber(),  topY.toNumber()]
        // 4. down to connector top (right side of left upright)
        const p4 = [leftInnerX.toNumber(),  connectorTopY.toNumber()]
        // 5. across connector top to left side of right upright
        const p5 = [rightInnerX.toNumber(), connectorTopY.toNumber()]
        // 6. up to top of right upright
        const p6 = [rightInnerX.toNumber(), topY.toNumber()]
        // 7. top-right outer
        const p7 = [rightOuterX.toNumber(), topY.toNumber()]
        // 8. bottom-right outer
        const p8 = [rightOuterX.toNumber(), bottomY.toNumber()]

        var combinedCutout = {
            paths: {
                // left outer vertical
                lineLeftOuter:    new makerjs.paths.Line(p1, p2),
                // left top
                lineLeftTop:      new makerjs.paths.Line(p2, p3),
                // left inner vertical (down to connector)
                lineLeftInner:    new makerjs.paths.Line(p3, p4),
                // connector top (left → right)
                lineConnectorTop: new makerjs.paths.Line(p4, p5),
                // right inner vertical (up)
                lineRightInner:   new makerjs.paths.Line(p5, p6),
                // right top
                lineRightTop:     new makerjs.paths.Line(p6, p7),
                // right outer vertical
                lineRightOuter:   new makerjs.paths.Line(p7, p8),
                // bottom (right → left)
                lineBottom:       new makerjs.paths.Line(p8, p1)
            }
        }

        if (generatorOptions.stabilizerFilletRadius.gt(0)) {
            const filletNum = generatorOptions.stabilizerFilletRadius.toNumber()

            // Outer corners
            combinedCutout.paths.filletBottomLeft  = makerjs.path.fillet(combinedCutout.paths.lineBottom, combinedCutout.paths.lineLeftOuter, filletNum)
            combinedCutout.paths.filletTopLeft     = makerjs.path.fillet(combinedCutout.paths.lineLeftOuter, combinedCutout.paths.lineLeftTop, filletNum)
            combinedCutout.paths.filletTopRight    = makerjs.path.fillet(combinedCutout.paths.lineRightTop, combinedCutout.paths.lineRightOuter, filletNum)
            combinedCutout.paths.filletBottomRight = makerjs.path.fillet(combinedCutout.paths.lineRightOuter, combinedCutout.paths.lineBottom, filletNum)

            // Inner corners at the top of the connector (where uprights meet the bar)
            combinedCutout.paths.filletInnerLeft   = makerjs.path.fillet(combinedCutout.paths.lineLeftInner, combinedCutout.paths.lineConnectorTop, filletNum)
            combinedCutout.paths.filletInnerRight  = makerjs.path.fillet(combinedCutout.paths.lineConnectorTop, combinedCutout.paths.lineRightInner, filletNum)

            // Top inner corners of the uprights
            combinedCutout.paths.filletLeftTopInner  = makerjs.path.fillet(combinedCutout.paths.lineLeftTop, combinedCutout.paths.lineLeftInner, filletNum)
            combinedCutout.paths.filletRightTopInner = makerjs.path.fillet(combinedCutout.paths.lineRightInner, combinedCutout.paths.lineRightTop, filletNum)
        }

        let cutouts = {
            models: {
                "stabilizer": combinedCutout
            }
        }

        if (!key.skipOrientationFix && key.height > key.width) {
            cutouts = makerjs.model.rotate(cutouts, -90)
        }

        return cutouts;
    }
}